import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { PageHeader } from '@/components/shared/page-header';
import { Pagination } from '@/components/shared/pagination';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';
import { useIsPlatformAdmin } from '@/hooks/use-is-platform-admin';
import { api, type OffsetPage } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';

interface SessionRequestRow {
  id: string;
  churchId: string;
  churchName?: string;
  reason: string;
  status: 'pending_platform' | 'pending_church' | 'approved' | 'active';
  impersonationId: string | null;
}

const statusVariant = (status: SessionRequestRow['status']): BadgeVariant => {
  if (status === 'active' || status === 'approved') return 'success';
  if (status === 'pending_church' || status === 'pending_platform') return 'warning';
  return 'neutral';
};

export const SupportAccessPage = () => {
  const { t } = useTranslation('admin');
  const { t: tCommon } = useTranslation('common');
  const { t: tErrors } = useTranslation('errors');
  const isAdmin = useIsPlatformAdmin();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const requests = useQuery({
    queryKey: ['platform-support-session-requests', page, pageSize],
    queryFn: () =>
      api.get<{ data: SessionRequestRow[]; page: OffsetPage }>('/platform/support/session-requests', {
        query: { page, pageSize },
      }),
    refetchInterval: 15_000,
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['platform-support-session-requests'] });
    await queryClient.invalidateQueries({ queryKey: ['platform-dashboard'] });
  };

  const joinRequest = useMutation({
    mutationFn: (requestId: string) => api.post(`/platform/support/session-requests/${requestId}/join`),
    onSuccess: async () => {
      toast.success(t('supportAccess.waitingForChurch'));
      await invalidate();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const openRequest = useMutation({
    mutationFn: (requestId: string) =>
      api.post<{ consoleUrl: string }>(`/platform/support/session-requests/${requestId}/open`, {}),
    onSuccess: (result) => {
      window.open(result.consoleUrl, '_blank', 'noopener');
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const revoke = useMutation({
    mutationFn: (grantId: string) => api.post(`/platform/support-access/${grantId}/revoke`, {}),
    onSuccess: async () => {
      toast.success(t('supportAccess.ended'));
      await invalidate();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const columns = useMemo((): DataTableColumn<SessionRequestRow>[] => {
    return [
      {
        id: 'church',
        header: t('supportAccess.columns.church'),
        cell: (row) => <span className="font-medium">{row.churchName}</span>,
      },
      {
        id: 'status',
        header: t('supportAccess.columns.state'),
        cell: (row) => (
          <Badge variant={statusVariant(row.status)}>{t(`supportAccess.requestStatuses.${row.status}`)}</Badge>
        ),
      },
      {
        id: 'reason',
        header: t('supportAccess.columns.reason'),
        cell: (row) => <span className="line-clamp-2">{row.reason}</span>,
      },
    ];
  }, [t]);

  const pageMeta = requests.data?.page;

  return (
    <div>
      <PageHeader title={t('supportAccess.title')} description={t('supportAccess.description')} />

      <DataTable
        rows={requests.data?.data ?? []}
        columns={columns}
        rowKey={(row) => row.id}
        loading={requests.isLoading}
        actionsClassName="w-[1%]"
        rowActions={
          isAdmin
            ? (row) => (
                <div className="flex justify-end gap-2 whitespace-nowrap">
                  {row.status === 'pending_platform' ? (
                    <Button size="sm" variant="outline" onClick={() => joinRequest.mutate(row.id)}>
                      {t('supportAccess.join')}
                    </Button>
                  ) : null}
                  {row.status === 'pending_church' ? (
                    <span className="text-body-sm text-fg-secondary">{t('supportAccess.waitingForChurch')}</span>
                  ) : null}
                  {row.status === 'approved' || row.status === 'active' ? (
                    <Button size="sm" loading={openRequest.isPending} onClick={() => openRequest.mutate(row.id)}>
                      {t('supportAccess.open')}
                    </Button>
                  ) : null}
                  {row.status === 'active' && row.impersonationId ? (
                    <Button
                      size="sm"
                      variant="outline"
                      loading={revoke.isPending && revoke.variables === row.impersonationId}
                      onClick={() => revoke.mutate(row.impersonationId!)}
                    >
                      {t('supportAccess.endVisit')}
                    </Button>
                  ) : null}
                </div>
              )
            : undefined
        }
        empty={
          <p className="py-8 text-center text-body-sm text-fg-secondary">{t('supportAccess.empty')}</p>
        }
      />
      {pageMeta && pageMeta.totalPages > 1 ? (
        <Pagination
          page={pageMeta.page}
          pageSize={pageMeta.pageSize}
          totalItems={pageMeta.totalItems}
          totalPages={pageMeta.totalPages}
          onPageChange={setPage}
          onPageSizeChange={(next) => {
            setPageSize(next);
            setPage(1);
          }}
          labels={{
            showing: ({ from, to, total }) => tCommon('pagination.showing', { from, to, total }),
            perPage: tCommon('pagination.perPage'),
            first: tCommon('pagination.first'),
            previous: tCommon('pagination.previous'),
            next: tCommon('pagination.next'),
            last: tCommon('pagination.last'),
          }}
        />
      ) : null}
    </div>
  );
};
